import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import * as _ from 'lodash';
import {
  CloudCustomer,
  CloudAccount,
  CloudEnvironment,
  DevOpsAction,
  DevOpsActionAndParameter,
  ErmDevOpsApiClientService,
  GetDevOpsActionsRequest} from 'erm-sdk';
import dayjs from 'dayjs';


@Component({
  standalone: false,
  selector: 'app-devops-actions-list',
  templateUrl: './devops-actions-list.component.html',
  styleUrls: ['./devops-actions-list.component.scss']
})
export class DevopsActionsListComponent implements OnInit {

  isLoading = false;

  devOpsActionGridOptions: GridOptions;
  devOpsActionGridApi?: GridApi;
  customerGridOptions: GridOptions;
  customerGridApi?: GridApi;
  accountGridOptions: GridOptions;
  accountGridApi?: GridApi;
  environmentGridOptions: GridOptions;
  environmentGridApi?: GridApi;

  selectedFetchOption?: string;
  selectedCustomer?: CloudCustomer;
  selectedAccount?: CloudAccount;
  selectedEnvironment?: CloudEnvironment;
  selectedRegion?: string;
  selectedDevOpsAction?: DevOpsAction;
  fetchDevOpsActionErrors?: string;

  cloudCutomers?: CloudCustomer[];
  cloudAccounts?: CloudAccount[];
  cloudEnvironments?: CloudEnvironment[];
  supportedRegions?: string[];
  devOpsActionsAndParameters?: DevOpsActionAndParameter[];

  constructor(private ermDevOpsApiClient: ErmDevOpsApiClientService, private router: Router) {

    // DevOps Ag-Grid Options
    this.devOpsActionGridOptions = {
      columnDefs: this.createDevOpsActionColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedDevOpsAction = _.first(event.api.getSelectedNodes().map(x => x.data));
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.devOpsActionGridApi = params.api;
      }
    };

    // Customers Ag-Grid Options
    this.customerGridOptions = {
      columnDefs: this.createCustomerColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedCustomer = _.first(event.api.getSelectedNodes().map(x => x.data));
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.customerGridApi = params.api;
      }
    };

    // Accounts Ag-Grid Options
    this.accountGridOptions = {
      columnDefs: this.createAccountColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedAccount = _.first(event.api.getSelectedNodes().map(x => x.data));
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.accountGridApi = params.api;
      }
    };

    // Environment Ag-Grid Options
    this.environmentGridOptions = {
      columnDefs: this.createEnvironmentColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedEnvironment = _.first(event.api.getSelectedNodes().map(x => x.data));
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.environmentGridApi = params.api;
      }
    };
  }

  async ngOnInit(): Promise<void> {
    try {

      this.isLoading = true;

      // Get customers
      const getCustomersResponse = await this.ermDevOpsApiClient.getCloudCustomers();
      this.cloudCutomers = getCustomersResponse.body?.cloudCustomers || new Array<CloudCustomer>();

      // Get accounts
      const getAccountsResponse = await this.ermDevOpsApiClient.getCloudAccounts();
      this.cloudAccounts = getAccountsResponse.body?.cloudAccounts || new Array<CloudAccount>();

      // Get environments
      const getEnvironmentsResponse = await this.ermDevOpsApiClient.getCloudEnvironments();
      this.cloudEnvironments = getEnvironmentsResponse.body?.cloudEnvironments || new Array<CloudEnvironment>();

      // Get Supported Regions
      const getSupportedRegions = await this.ermDevOpsApiClient.getSupportedRegions();
      this.supportedRegions = getSupportedRegions.body?.regions || [];

      // Initialize ag-grid
      this.devOpsActionGridApi?.setGridOption('rowData', new Array<DevOpsAction>());
      this.customerGridApi?.setGridOption('rowData', this.cloudCutomers);
      this.accountGridApi?.setGridOption('rowData', this.cloudAccounts);
      this.environmentGridApi?.setGridOption('rowData', this.cloudEnvironments);
    } finally {
      this.isLoading = false;
    }
  }

  private createDevOpsActionColumnDefs(): any[] {
    const router = this.router;
    return [
      {
        headerName: 'EventRule Id', field: 'eventRuleId',
      },
      { headerName: 'Action Name', field: 'actionName', width: 300 },
      { headerName: 'Schedule Description', field: 'scheduleDescription' },
      {
        headerName: 'Customer Id', field: 'customerId',
        cellRenderer: (params: any) => {
          const url = router.createUrlTree(['erm/customers', params.value]);
          return `<a href="#${url}">${params.value}</a>`;
        },
      },
      {
        headerName: 'Customer Name',
        field: 'customerId',
        width: 300,
        cellRenderer: (params: any) => {
          const customer = this.cloudCutomers?.find(x => x.customerId === params.value);
          return `<span>${customer?.name}</span>`;
        },
      },
      { headerName: 'Account Id', field: 'accountId' },
      { headerName: 'Environment Id', field: 'environmentId' },
      {
        headerName: 'Environment Version',
        field: 'environmentId',
        cellRenderer: (params: any) => {
          if (params.value) {
            const environment = this.cloudEnvironments?.find(x => x.environmentId === params.value);
            return `<span>${environment?.version}</span>`;
          }
          else {
            return ``;
          }
        },
      },
      { headerName: 'Region', field: 'region' },
      { headerName: 'Schedule Expression', field: 'scheduleExpression' },
      { headerName: 'Trigger Time', field: 'triggerTime' }
    ];
  }

  private createCustomerColumnDefs(): any[] {
    const router = this.router;
    return [
      {
        headerName: 'Customer Id',
        field: 'customerId',
        cellRenderer: (params: any) => {
          const url = router.createUrlTree(['erm/customers', params.value]);
          return `<a href="#${url}">${params.value}</a>`;
        },
      },
      { headerName: 'Name', field: 'name' },
      { headerName: 'Description', field: 'description', width: 300 },
      { headerName: 'Connect Account Id', field: 'connectAccountId', width: 300 },
      {
        headerName: 'Created', field: 'creationTime',
        cellRenderer: (params: any) => {
          return dayjs(params.value).format('YYYY-MM-DD HH:mm:ss');
        },
      }
    ];
  }

  private createAccountColumnDefs(): any[] {
    return [
      {
        headerName: 'Account Id',
        field: 'accountId'
      },
      { headerName: 'Description', field: 'description', width: 300 },
      {
        headerName: 'Customer Name',
        field: 'customerId',
        width: 300,
        cellRenderer: (params: any) => {
          const customer = this.cloudCutomers?.find(x => x.customerId === params.value);
          return `<span>${customer?.name}</span>`;
        },
      },
      { headerName: 'Region', field: 'region' },
      { headerName: 'Maintenance', field: 'inMaintenanceMode' },
      { headerName: 'Enabled', field: 'isEnabled' },
      { headerName: 'Connect Account Id', field: 'connectAccountId', width: 300 },
      {
        headerName: 'Created', field: 'creationTime',
        cellRenderer: (params: any) => {
          return dayjs(params.value).format('YYYY-MM-DD HH:mm:ss');
        }
      }
    ];
  }

  private createEnvironmentColumnDefs(): any[] {
    const router = this.router;
    return [
      {
        headerName: 'Environment Id',
        field: 'environmentId',
        cellRenderer: (params: any): string => {
          const url = router.createUrlTree(['erm/environments', params.value]);
          return `<a href="#${url}">${params.value}</a>`;
        },
        width: 140
      },
      { headerName: 'Version', field: 'version' },
      { headerName: 'Name', field: 'name' },
      { headerName: 'Description', field: 'description', width: 300 },
      {
        headerName: 'Production',
        field: 'isProduction',
        cellStyle: (params: any) => {
          if (params.value === true) {
            return {
              backgroundColor: 'yellow'
            };
          } else {
            return null;
          }
        },
        width: 140
      },
      {
        headerName: 'Customer Name',
        field: 'customerId',
        width: 300,
        cellRenderer: (params: any) => {
          const customer = this.cloudCutomers?.find(x => x.customerId === params.value);
          return `<span>${customer?.name}</span>`;
        }
      },
      {
        headerName: 'Account Description',
        field: 'accountId',
        width: 300,
        cellRenderer: (params: any) => {
          const account = this.cloudAccounts?.find(x => x.accountId === params.value);
          return `<span>${account?.description}</span>`;
        }
      },
      { headerName: 'Region', field: 'region', width: 140 },
      { headerName: 'Schema', field: 'schemaName' },
      { headerName: 'Deployment Type', field: 'deploymentType', width: 250 },
      { headerName: 'Maintenance', field: 'inMaintenanceMode' },
      { headerName: 'Available', field: 'isAvailable' },
      { headerName: 'Enabled', field: 'isEnabled' },
      { headerName: 'Status', field: 'statusMessage' },
      { headerName: 'Connect Account Id', field: 'connectAccountId', width: 300 },
      { headerName: 'EMS Port', field: 'emsServicePort' },
      {
        headerName: 'Created', field: 'creationTime',
        cellRenderer: (params: any) => {
          return dayjs(params.value).format('YYYY-MM-DD HH:mm:ss');
        }
      },
    ];
  }

  onGetDevOpsActionParameterChanged(parameter: string | undefined): void {
    switch (parameter) {
      case 'Customer':
        this.selectedAccount = undefined;
        this.selectedEnvironment = undefined;
        this.selectedRegion = undefined;
        break;
      case 'Account':
        this.selectedCustomer = undefined;
        this.selectedEnvironment = undefined;
        this.selectedRegion = undefined;
        break;
      case 'Environment':
        this.selectedCustomer = undefined;
        this.selectedAccount = undefined;
        this.selectedRegion = undefined;
        break;
      case 'Region':
        this.selectedCustomer = undefined;
        this.selectedAccount = undefined;
        this.selectedEnvironment = undefined;
        break;
      default:
        this.selectedCustomer = undefined;
        this.selectedAccount = undefined;
        this.selectedEnvironment = undefined;
        this.selectedRegion = undefined;
        this.selectedFetchOption = undefined;
        break;
    }
  }

  clearSelection(): void {
    this.onGetDevOpsActionParameterChanged('All');
  }

  async getDevOpsActions(): Promise<void> {
    try {
      this.isLoading = true;
      this.devOpsActionGridOptions['api']?.showLoadingOverlay();
      const getDevopsActionResponse = await this.ermDevOpsApiClient.getDevOpsActions({
        customerId: this.selectedCustomer?.customerId,
        accountId: this.selectedAccount?.accountId,
        environmentId: this.selectedEnvironment?.environmentId,
        region: this.selectedRegion
      } as GetDevOpsActionsRequest);
      const devopsActions = getDevopsActionResponse.body?.eventRules || new Array<DevOpsAction>();
      this.fetchDevOpsActionErrors = getDevopsActionResponse.body?.error;
      // Initialize DevOps Action grid
      this.devOpsActionGridApi?.setGridOption('rowData', devopsActions);
    }
    finally {
      this.isLoading = false;
    }
  }

  async deleteDevOpsAction(): Promise<void> {
    try {
      this.isLoading = true;

      const eventRuleId = this.selectedDevOpsAction?.eventRuleId as string;
      await this.ermDevOpsApiClient.deleteDevOpsAction(eventRuleId);
      // remove selected item from devops action grid
      this.devOpsActionGridOptions['api']?.applyTransaction({
        remove: [this.selectedDevOpsAction]
      });
      this.selectedDevOpsAction = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  canDeleteDevOpsAction(): boolean {
    return !this.selectedDevOpsAction;
  }
}
